import React, { useEffect, useState, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList } from 'recharts';
import { FaUsers, FaUserPlus, FaFileSignature, FaFileMedicalAlt, FaChartBar, FaChartPie } from "react-icons/fa";

import '../../../css/UserAdmin/AnalyticsSection.css';
import CardModule from '../CardModule';

const EmptyChartState = ({ message }) => (
  <div className="empty-chart-state">
    <p>{message}</p>
  </div>
);

const RegistrarAnalytics = ({ hasAnalyticsAccess }) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearsLoading, setYearsLoading] = useState(true);
  const [yearsError, setYearsError] = useState(null);

  const [loading, setLoading] = useState(false);
  const [enrollmentStats, setEnrollmentStats] = useState({
    totalEnrolled: 0, newStudents: 0, forValidation: 0, withWaiver: 0,
  });
  const [enrollmentTrendData, setEnrollmentTrendData] = useState([]);
  const [strandEnrollmentData, setStrandEnrollmentData] = useState([]);

  const COLORS = ['#2ecc71', '#3498db', '#e74c3c', '#f39c12', '#9b59b6', '#34495e'];

  useEffect(() => {
    const fetchAcademicYears = async () => {
      setYearsLoading(true); setYearsError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/years?includeArchived=true&includeInactive=true`);
        if (!response.ok) throw new Error('Failed to fetch academic years');
        const data = await response.json();
        const years = data.data || [];
        setAcademicYears(years);
        const activeYear = years.find(year => year.status === 'Active');
        if (activeYear) setSelectedYear(activeYear.Year);
        else if (years.length > 0) setSelectedYear(years[0].Year);
      } catch (error) {
        console.error("Error fetching academic years:", error);
        setYearsError('Could not load academic years.');
      } finally {
        setYearsLoading(false);
      }
    };
    fetchAcademicYears();
  }, []);

  const fetchRegistrarAnalytics = useCallback(async (year) => {
    if (!year) return;
    setLoading(true);
    try {
      const [cardResponse, trendResponse, distResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/enrollments/analytics?academicYear=${encodeURIComponent(year)}`),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/enrollments/yearly-trend`),
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/enrollments/strand-distribution?academicYear=${encodeURIComponent(year)}`)
      ]);
      if (cardResponse.ok) {
        const cardResult = await cardResponse.json();
        if (cardResult.success) setEnrollmentStats(cardResult.data);
      } else {
        console.error("Failed to fetch card analytics");
        setEnrollmentStats({ totalEnrolled: 0, newStudents: 0, forValidation: 0, withWaiver: 0 });
      }
      if (trendResponse.ok) {
        const trendResult = await trendResponse.json();
        if (trendResult.success) setEnrollmentTrendData(trendResult.data);
      } else {
        console.error("Failed to fetch yearly trend data");
        setEnrollmentTrendData([]);
      }
      if (distResponse.ok) {
        const distResult = await distResponse.json();
        if (distResult.success) setStrandEnrollmentData(distResult.data);
      } else {
        console.error("Failed to fetch strand distribution data");
        setStrandEnrollmentData([]);
      }
    } catch (error) {
      console.error("Error fetching registrar analytics:", error);
      setEnrollmentStats({ totalEnrolled: 0, newStudents: 0, forValidation: 0, withWaiver: 0 });
      setEnrollmentTrendData([]);
      setStrandEnrollmentData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAnalyticsAccess && selectedYear) {
      fetchRegistrarAnalytics(selectedYear);
    }
  }, [selectedYear, hasAnalyticsAccess, fetchRegistrarAnalytics]);

  if (!hasAnalyticsAccess) return null;

  return (
    // --- MODIFICATION IS HERE ---
    <div className="analytics-section registrar-analytics">
      <div className="analytics-header">
        <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '16px' }}>Registrar Analytics</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="academic-year-selector-registrar">Academic Year:</label>
          <select id="academic-year-selector-registrar" className="timeframe-selector" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} disabled={yearsLoading || loading}>
            {yearsLoading ? <option>Loading...</option> : yearsError ? <option>{yearsError}</option> :
              academicYears.map(year => (
                <option key={year._id} value={year.Year}>
                  {year.Year} {year.status === 'Active' && '(Active)'}
                </option>
              ))
            }
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-analytics-indicator">Loading enrollment data...</div>
      ) : (
        <>
          <div className="card-container" style={{ marginBottom: '2rem' }}>
            <CardModule title="Total Enrolled" description="All officially enrolled students" statistics={enrollmentStats.totalEnrolled.toString()} icon={FaUsers} path={'/admin/manage-enrollment'}/>
            <CardModule title="New Students" description="First-time enrollees" statistics={enrollmentStats.newStudents.toString()} icon={FaUserPlus} path={'/admin/manage-enrollment'}/>
            <CardModule title="Continuing Students" description="old students" statistics={enrollmentStats.forValidation.toString()} icon={FaFileSignature} path={'/admin/manage-enrollment'}/>
            <CardModule title="With Waivers" description="Enrollees with waived requirements" statistics={enrollmentStats.withWaiver.toString()} icon={FaFileMedicalAlt} path={'/admin/manage-enrollment'}/>
          </div>

          <div className="charts-row-container">
            <div className="chart-card">
              <h3 className="chart-title"><FaChartPie /> Enrollees Per Strand</h3>
              <p className="chart-description">Enrollment Distribution By Strand</p>
              {strandEnrollmentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={strandEnrollmentData} cx="50%" cy="50%" labelLine={true} label={({ name, value }) => `${name}: ${value}`} outerRadius={85} fill="#8884d8" dataKey="value" >
                      {strandEnrollmentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (<EmptyChartState message={`No enrollment data found for ${selectedYear}.`} />)}
            </div>

            <div className="chart-card">
              <h3 className="chart-title"><FaChartBar /> No. of Enrollees Per Year</h3>
              <p className="chart-description">Enrollment Trend Analysis</p>
              {enrollmentTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={enrollmentTrendData} margin={{ top: 20, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="enrollees" name="Total Enrollees" fill={COLORS[0]}>
                      <LabelList dataKey="enrollees" position="top" style={{ fill: '#34495e', fontSize: '14px' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (<EmptyChartState message="No enrollment trend data available." />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RegistrarAnalytics;